import tccf7StateManager from './tccf7StateManager.js';
import tccf7SessionManager from './tccf7SessionManager.js';
import { TCCF7_CONFIG } from './tccf7Constants.js';
import { tccf7Logger } from './tccf7ErrorHandler.js';

/*-------------------------------------------------------------------------------

  シングルトン：ボタン制御（確認ボタン、戻るボタン、送信ボタン）

--------------------------------------------------------------------------------*/

export default class tccf7ButtonController {
  static #instances = new Map();

  /*----------------------------------------
    インスタンスを取得する静的メソッド
  ------------------------------------------*/
  /**
   * @param {string} formId 
   * @param {HTMLElement} form // 第2引数を追加
   */
  static getInstance(formId, form) {
    if (!this.#instances.has(formId)) {
      // 初回のみ form をコンストラクタに渡す
      this.#instances.set(formId, new tccf7ButtonController(formId, form));
    }
    return this.#instances.get(formId);
  }

  constructor(formId, form) {
    this.formId = formId;
    this.form = form;
    if (!this.form) return;

    this.submitBtnValue = '送信';
    this.stateManager = tccf7StateManager.getInstance(this.formId, this.form);

    // イベントリスナーの保持用
    // key: btnName, value: { element, callback }
    this.handlers = new Map();
    this.init();
  }

  /*----------------------------------------
    初期設定
  ------------------------------------------*/
  init() {
    if (!this.form) return;

    this.refs(this.form);
    this.setInputViewDisabled();
    this.sessionManager = new tccf7SessionManager(this.formId, this.btns.confirm, this.form);

    document.addEventListener('tccf7:statusUpdate', (e) => {
      if (e.detail.formId === this.formId) {
        this.syncWithState(e.detail.state);
      }
    });

    this.setConfirmClickHandler(async () => {
      this.setAllDisabled(true);
      const result = await this.sessionManager.checkNonce();
      if (result.success) {
        this.stateManager.setValidate(false);
        this.stateManager.setVerified(true);
        this.stateManager.setConfirm();
      } else {
        this.stateManager.setVerified(false);
        this.stateManager.setInput();
      }
    });

    this.setupHandler('back', async () => {
      this.stateManager.setVerified(false);
      this.stateManager.setValidate(false);
      this.clearValidationOutput();
      this.stateManager.setInput();
      await this.sessionManager.clearConfirmReady();
    });

    this.setSubmitClickHandler(() => {
      if (this.stateManager.redirect) return;
      if (!this.stateManager.isSpam && this.stateManager.isVerified && !this.stateManager.isValidate) {
        this.stateManager.setComplete();
      }
    });
  }

  /*----------------------------------------
    要素を取得
  ------------------------------------------*/
  refs(form) {
    if (!form) return;

    const selectors = TCCF7_CONFIG.SELECTORS.BTN;
    this.btns = {
      confirm: form.querySelector(selectors.CONFIRM),
      back: form.querySelector(selectors.BACK),
      submit: form.querySelector(selectors.SUBMIT)
    };
  }

  /*----------------------------------------
    要素の参照を更新（Observerから呼ばれる想定）
  ------------------------------------------*/
  updateRefs(form) {
    this.refs(form);

    // sessionManager の confirmBtn も更新（動的に追加されたボタンへ対応）
    if (this.sessionManager && this.btns.confirm) {
      this.sessionManager.refs(this.btns.confirm);
    }

    this.handlers.forEach((handler, btnKey) => {
      const newBtn = this.btns[btnKey];
      if (newBtn) {
        // 古い参照（もしあれば）を念のため掃除して、新しい要素に貼り直す
        newBtn.removeEventListener('click', handler.callback);
        newBtn.addEventListener('click', handler.callback);
      }
    });

    // 元のボタンテキストを保存（初回のみ）
    if (this.btns.submit && this.submitBtnValue === '送信') {
      const val = this.btns.submit.value;
      if (val && !['送信中', '送信完了', '送信失敗'].includes(val)) {
        this.submitBtnValue = val;
      }
    }

    // 動的に追加されたボタンに現在のステートを反映する
    const currentState = this.stateManager?.getState();
    if (currentState) {
      this.syncWithState(currentState);
    }
  }

  /*----------------------------------------
    状態に基づいたUIの自動同期
  ------------------------------------------*/
  syncWithState(state) {
    if (!this.btns) return;

    switch (state) {
      case 'input':
        this.resetSubmitButton();
        this.setInputViewDisabled();
        this.clearValidationOutput();
        tccf7Logger.clearUserError(this.form);
        break;
      case 'confirm':
        this.resetSubmitButton();
        this.setConfirmViewDisabled();
        tccf7Logger.clearUserError(this.form);
        break;
      case 'complete':
        tccf7ButtonController.setButtonDisabled(this.btns.confirm, true);
        tccf7ButtonController.setButtonDisabled(this.btns.back, true);
        tccf7Logger.clearUserError(this.form);
        break;
    }
  }

  /*----------------------------------------
    イベントハンドラー設定（重複登録を自動回避）
  ------------------------------------------*/
  setupHandler(btnKey, callback) {
    this.handlers.set(btnKey, { callback });

    const btn = this.btns[btnKey];
    if (btn) {
      btn.removeEventListener('click', callback);
      btn.addEventListener('click', callback);
    }
  }

  setConfirmClickHandler(callback) { this.setupHandler('confirm', callback); }
  setSubmitClickHandler(callback) { this.setupHandler('submit', callback); }

  /*----------------------------------------
    送信ボタン：表示状態の操作
  ------------------------------------------*/
  updateSubmitStatus(text, disabled) {
    const btn = this.btns?.submit;
    if (!btn) return;
    btn.value = text;
    tccf7ButtonController.setButtonDisabled(btn, disabled);
  }

  resetSubmitButton() { this.updateSubmitStatus(this.submitBtnValue, false); }
  setSubmitting() { this.updateSubmitStatus('送信中', true); }
  setCompleted() { this.updateSubmitStatus('送信完了', true); }
  setFailed() { this.updateSubmitStatus('送信失敗', true); }

  /*----------------------------------------
    全ボタン：一括活性・非活性
  ------------------------------------------*/
  setAllDisabled(disabled) {
    if (!this.btns) return;
    Object.values(this.btns).forEach(btn => {
      tccf7ButtonController.setButtonDisabled(btn, disabled);
    });
  }

  setInputViewDisabled() {
    tccf7ButtonController.setButtonDisabled(this.btns.confirm, false);
    tccf7ButtonController.setButtonDisabled(this.btns.back, true);
    tccf7ButtonController.setButtonDisabled(this.btns.submit, true);
  }
  setConfirmViewDisabled() {
    tccf7ButtonController.setButtonDisabled(this.btns.confirm, true);
    tccf7ButtonController.setButtonDisabled(this.btns.back, false);
    tccf7ButtonController.setButtonDisabled(this.btns.submit, false);
  }

  clearValidationOutput() {
    if (!this.form) return;
    this.form.classList.remove('tccf7-show-response-output');
  }

  /*----------------------------------------
    各ボタン：活性・非活性：静的メソッド
  ------------------------------------------*/
  static setButtonDisabled(button, disabled) {
    if (!button) return;
    const id = button.id;
    const label = id ? document.querySelector(`label[for="${id}"]`) : null;
    const wrapper = label || button.closest('label') || button.closest(TCCF7_CONFIG.SELECTORS.LABEL_WRAPPER);
    if (disabled) {
      button.setAttribute('disabled', 'disabled');
      button.classList.add('is-disabled');
      wrapper?.classList.add('is-disabled');
    } else {
      button.removeAttribute('disabled');
      button.classList.remove('is-disabled');
      wrapper?.classList.remove('is-disabled');
    }
  }
}